import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Bell, Search, Menu, ListChecks } from "lucide-react";
import { UserMenu } from "./UserMenu";
import { CommandPalette } from "./CommandPalette";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useUIStore } from "@/store/uiStore";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function EnvBadge() {
  const isDev = import.meta.env.DEV;
  if (!isDev) return null;
  return (
    <span className="inline-flex items-center rounded-full border border-status-warning/30 bg-status-warning/10 px-2 py-0.5 text-xs font-medium text-status-warning">
      Dev
    </span>
  );
}

/**
 * Compact "Setup N/6" thread that follows the user outside the dashboard, so
 * onboarding progress stays visible while working deep in another screen.
 * Hidden with the same guard as SetupChecklist (dismissed or all steps done).
 */
function SetupProgress() {
  const { data } = useOnboarding();
  if (!data) return null;

  const { data: onboarding, dismissed } = data;
  if (dismissed || onboarding.completed_count >= onboarding.total_count) return null;

  return (
    <Link
      to="/dashboard"
      className={cn(
        "hidden sm:inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2.5 py-1.5 text-xs font-medium text-muted-foreground",
        "hover:bg-muted hover:text-foreground transition-colors"
      )}
      aria-label={`Setup ${onboarding.completed_count} of ${onboarding.total_count} steps complete. Resume setup.`}
    >
      <ListChecks className="h-3.5 w-3.5" />
      Setup {onboarding.completed_count}/{onboarding.total_count}
    </Link>
  );
}

export function TopNav() {
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs(location.pathname);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);

  return (
    <>
      <header className={cn(
        "sticky top-0 z-20 h-14 border-b flex items-center justify-between px-4 sm:px-6 gap-2 sm:gap-4",
        "bg-card/80 backdrop-blur-md"
      )}>
        {/* Mobile: open navigation drawer */}
        <button
          onClick={() => setMobileNavOpen(true)}
          className={cn(
            "lg:hidden shrink-0 -ml-1 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          )}
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm min-w-0 flex-1">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1 min-w-0">
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              {crumb.href ? (
                <Link
                  to={crumb.href}
                  className="text-muted-foreground hover:text-foreground transition-colors truncate"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-foreground font-medium truncate">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>

        {/* Right side controls */}
        <div className="flex items-center gap-2 shrink-0">
          <SetupProgress />
          <EnvBadge />

          {/* Cmd+K trigger */}
          <button
            onClick={() => {
              const event = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
              document.dispatchEvent(event);
            }}
            className={cn(
              "hidden sm:flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground",
              "hover:bg-muted hover:text-foreground transition-colors"
            )}
            aria-label="Open command palette"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Search</span>
            <kbd className="ml-1 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] leading-none">
              ⌘K
            </kbd>
          </button>

          {/* Notifications — reserved.
              The trigger is a span, not the button: a `disabled` button receives no
              pointer events, so Radix never saw a hover and this tooltip could never fire. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} aria-label="Notifications (coming soon)" className="inline-flex">
                <span
                  aria-hidden
                  className="p-1.5 rounded-md text-muted-foreground transition-colors opacity-50 cursor-not-allowed"
                >
                  <Bell className="h-4 w-4" />
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent>Notifications coming soon</TooltipContent>
          </Tooltip>

          <UserMenu />
        </div>
      </header>
      <CommandPalette />
    </>
  );
}
